import React, { useEffect, useState } from 'react';
import { Badge, Button, Card } from 'react-bootstrap';
import { MdContentCopy, MdOutlineModeEdit } from 'react-icons/md';
import { possiblyPluralString, userFriendlyDateString } from '../../libs/miscLib';
import { TbFileDownload } from 'react-icons/tb';
import { GrDocumentTxt } from 'react-icons/gr';
import EditWordlistModal from './EditWordlistModal';
import { downloadActiveWordlist, getCurrentWordlistObj, getTotalWordCount, useSwdContext } from '../../libs/autofillLib';
import { FaRegHandPointRight } from 'react-icons/fa6';
import LoaderButton from '../reusable/LoaderButton';
import { BsTrash } from 'react-icons/bs';
import { del } from 'aws-amplify/api';
import { useToastNotifications } from '../../libs/toastLib';
import ViewWordlistModal from './ViewWordlistModal';


export default function WordlistCard({ wordlist, triggerRefresh }) {

  const { postErrorNotification } = useToastNotifications();

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [isSettingActive, setIsSettingActive] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Keep track of currently selected wordlist, based on the swdVersion
  const { swdVersion, fetchDifferentWordlist } = useSwdContext();
  const [activeWordlist, setActiveWordlist] = useState(null);
  useEffect(() => {
    setActiveWordlist(getCurrentWordlistObj());
  }, [swdVersion]);

  const isActive = activeWordlist && activeWordlist.wordlistId === wordlist?.wordlistId;
  
  // If activeWordlist is available, use that for most accurate stats (non-active wordlists could have inexact word stats due to unmerged mods)
  const myWordlist = isActive ? (activeWordlist || wordlist) : wordlist;

  // Calculate total stats using both base and mods
  const baseStats = myWordlist?.baseWordlist?.stats || {};
  const wordCount = isActive ? getTotalWordCount() : baseStats.wordCount + myWordlist?.mods?.length;
  const modScores = (myWordlist?.mods || []).map(o => parseInt(o.split(';')?.[1]?.trim())).filter(s => !isNaN(s));
  const minScore = Math.min(baseStats.minScore, ...modScores);
  const maxScore = Math.max(baseStats.maxScore, ...modScores);

  // Permissions level ("MANAGE", "EDIT", or "VIEW") defines what buttons are shown
  const permissionsLevel = myWordlist?.permissionsLevel;

  return (
    <Card
     className={`not-too-wide position-relative shadow-${isActive ? 'lg border-2' : 'sm'}`}
     style={{ backgroundColor: isActive ? '#e0f9ec' : '#f8fcfc' }}
     key={`wordlist-card-${myWordlist?.wordlistId}`}
    >
      {isActive && (
        <div
          className="position-absolute top-0 start-0 bg-success text-white px-2 py-1 small fw-bold rounded-end"
          style={{ transform: 'translate(-5px, -50%)', zIndex: 1 }}
        >
          ACTIVE
        </div>
      )}

      <Card.Body className='d-flex flex-column'>
        <Card.Title className='h6 mb-0 fw-bold d-flex align-items-center gap-2'>
          {myWordlist?.name || '(untitled wordlist)'}
          {myWordlist?.isCommunity && <Badge className='ms-auto' bg='success'>COMMUNITY</Badge>}
        </Card.Title>

        {(myWordlist?.author || myWordlist?.description) && (
          <div className='text-muted small'>
            {`${myWordlist?.author || ''}${myWordlist?.author && myWordlist?.description ? '  ⋅  ' : ''}${myWordlist?.description || ''}`}
          </div>
        )}

        <div className='d-flex gap-2 align-items-center text-success mt-2'>
          <div className='d-flex align-items-center gap-1 mb-1 p-1 border border-success rounded-1'>
            <GrDocumentTxt />
            {myWordlist?.baseWordlist ? (
              <span className='text-xs'>{myWordlist?.baseWordlist?.filename}</span>
            ) : (
              <span className='text-xs text-danger fst-italic'>No file found</span>
            )}
          </div>
          {(myWordlist?.mods?.length || !myWordlist?.isCommunity) && (
            <div className='fw-bold small text-success'>+ {possiblyPluralString(myWordlist?.mods.length, 'mod')}</div>
          )}
        </div>

        <div className='mb-3'>
          {wordCount && !isNaN(minScore) && !isNaN(maxScore) ? (
            <>
              <div><span className='fw-bold'>{isActive ? '' : '~'}{wordCount.toLocaleString('en-US')}</span> <span className='small text-muted'>words</span></div>
              <div><span className='fw-bold'>{minScore}&ndash;{maxScore}</span> <span className='small text-muted'>scores range</span></div>
            </>
          ) : !myWordlist?.baseWordlist ? (
            <div className='text-danger fst-italic small'>No base file found for this wordlist.<br />The file may have been deleted.</div>
          ) : (
            <div className='text-muted fst-italic small'>Stats not available yet. Refresh in a few moments.</div>
          )}
        </div>

        {!myWordlist.isCommunity && <>
          <div className='text-xs text-muted mt-auto'>Created {userFriendlyDateString(myWordlist?.createdAt)}</div>
          {myWordlist?.createdAt !== myWordlist?.modifiedAt && (
            <div className='text-xs text-muted'>Modified {userFriendlyDateString(myWordlist?.modifiedAt)}</div>
          )}
        </>}

        <div className='d-flex gap-2 mt-2'>
          {isActive ? (
            <>
              <Button
                className='py-0 d-flex align-items-center gap-1 zoomable'
                size='sm'
                variant='success'
                onClick={() => setShowEditModal(true)}
              ><MdOutlineModeEdit />{'View' + (permissionsLevel === 'MANAGE' || permissionsLevel === 'EDIT' ? '/Edit' : '')}</Button>
              <Button
                className='py-0 d-flex align-items-center gap-1 zoomable'
                size='sm'
                variant='outline-primary'
                onClick={() => setShowDuplicateModal(true)}
              ><MdContentCopy />Duplicate</Button>
              <Button
                className='py-0 d-flex align-items-center gap-1 zoomable'
                size='sm'
                variant='outline-secondary'
                onClick={() => downloadActiveWordlist()}
              ><TbFileDownload />Download</Button>
            </>
          ) : (
            <>
              <LoaderButton
                className='py-0 d-flex align-items-center gap-1 zoomable'
                isLoading={isSettingActive}
                size='sm'
                variant='info'
                disabled={!myWordlist?.baseWordlist}
                onClick={async () => {
                  setIsSettingActive(true);
                  await fetchDifferentWordlist(myWordlist);
                  setIsSettingActive(false);
                }}
              ><FaRegHandPointRight />Make Active</LoaderButton>

              {!myWordlist?.isCommunity && (
                <LoaderButton
                  className='ms-auto py-0 d-flex align-items-center gap-1 zoomable'
                  isLoading={isDeleting}
                  size='sm'
                  variant='outline-danger'
                  onClick={async () => {
                    if (!window.confirm('Are you sure you want to delete this wordlist?')) return;
                    setIsDeleting(true);
                    try {
                      await del({
                        apiName: 'wordlists',
                        path: `/wordlists/${myWordlist.wordlistId}`,
                      }).response;
                    } catch (e) {
                      console.log(e);
                      postErrorNotification('There may have been an error trying to remove your wordlist!', 'Please get in touch if this continues.');
                    }
                    setIsDeleting(false);
                    if (triggerRefresh) triggerRefresh();
                  }}
                ><BsTrash /></LoaderButton>
              )}
            </>
          )}
        </div>
      </Card.Body>

      {isActive && <>
        {(permissionsLevel === 'EDIT' || permissionsLevel === 'MANAGE') ? (
          <EditWordlistModal show={showEditModal} onHide={() => setShowEditModal(false)} triggerRefresh={triggerRefresh} />
        ) : (
          <ViewWordlistModal show={showEditModal} onHide={() => setShowEditModal(false)} />
        )}
        <EditWordlistModal show={showDuplicateModal} onHide={() => setShowDuplicateModal(false)} triggerRefresh={triggerRefresh} createNew={true} fromActive={true} />
      </>}
    </Card>
  );
}